import {
  GEO_SIM_PARAM,
  GEO_SIM_TOKEN_PARAM,
  type GeoSimSession,
  type GeoSimVpnMode,
} from "./constants";
import { readGeoSimSession } from "./session";

export interface BuildGeoSimUrlInput {
  path?: string;
  country: string;
  token: string;
  state?: string;
  province?: string;
  city?: string;
  neighborhood?: string;
  simulateVpn?: GeoSimVpnMode;
  forceVariant?: string;
  lang?: string;
  simulateIp?: string;
  /** Preserve non-sim query keys from current location */
  preserveOtherParams?: boolean;
}

/**
 * Build hard-nav URL for edge override.
 * Primary transport: geo_sim=1 & gst & country (+ optional geo fields).
 */
export function buildGeoSimPreviewUrl(input: BuildGeoSimUrlInput): string {
  const path =
    input.path && input.path.startsWith("/")
      ? input.path
      : typeof window !== "undefined"
        ? window.location.pathname || "/"
        : "/";

  const params = new URLSearchParams();
  if (input.preserveOtherParams && typeof window !== "undefined") {
    const current = new URLSearchParams(window.location.search);
    current.forEach((v, k) => {
      if (
        k === GEO_SIM_PARAM ||
        k === GEO_SIM_TOKEN_PARAM ||
        k === "country" ||
        k === "state" ||
        k === "province" ||
        k === "city" ||
        k === "neighborhood" ||
        k === "simulate_vpn" ||
        k === "simulate_ip" ||
        k === "_force_variant" ||
        k === "lang"
      ) {
        return;
      }
      params.set(k, v);
    });
  }

  params.set(GEO_SIM_PARAM, "1");
  params.set(GEO_SIM_TOKEN_PARAM, input.token);
  params.set("country", input.country.toUpperCase());
  if (input.state) params.set("state", input.state.toUpperCase());
  if (input.province) params.set("province", input.province);
  if (input.city) params.set("city", input.city);
  if (input.neighborhood) params.set("neighborhood", input.neighborhood);
  const vpn = input.simulateVpn || "clean";
  params.set("simulate_vpn", vpn);
  if (input.simulateIp) params.set("simulate_ip", input.simulateIp);
  if (input.forceVariant) params.set("_force_variant", input.forceVariant);
  if (input.lang) params.set("lang", input.lang);

  const q = params.toString();
  return `${path}${q ? `?${q}` : ""}`;
}

export function buildGeoSimExitUrl(path?: string): string {
  const p =
    path && path.startsWith("/")
      ? path
      : typeof window !== "undefined"
        ? window.location.pathname || "/"
        : "/";
  // Drop all sim params; keep UTM-less clean home for real geo
  return p.split("?")[0] || "/";
}

/**
 * Re-attach incomplete geo_sim params after SPA navigation (replaceState only).
 * Does not touch edge config — only ensures next hard reload / API polls see intent.
 */
export function pinGeoSimParamsIfNeeded(): boolean {
  if (typeof window === "undefined") return false;
  const session = readGeoSimSession();
  if (!session) return false;

  const params = new URLSearchParams(window.location.search);
  const hasFlag = params.get(GEO_SIM_PARAM) === "1";
  const hasGst = !!params.get(GEO_SIM_TOKEN_PARAM);
  const hasCountry = !!params.get("country");

  if (hasFlag && hasGst && hasCountry) {
    // Still ensure country/gst match session (SPA may have partial params)
    const urlCountry = (params.get("country") || "").toUpperCase();
    const urlGst = params.get(GEO_SIM_TOKEN_PARAM) || "";
    if (urlCountry === session.country && urlGst === session.token) {
      return false;
    }
  }

  const next = buildGeoSimPreviewUrl({
    path: window.location.pathname,
    country: session.country,
    token: session.token,
    state: session.state,
    province: session.province,
    city: session.city,
    neighborhood: session.neighborhood,
    simulateVpn: session.simulateVpn,
    simulateIp: session.simulateIp,
    preserveOtherParams: true,
  });

  const target = `${window.location.pathname}${next.includes("?") ? next.slice(next.indexOf("?")) : ""}`;
  if (`${window.location.pathname}${window.location.search}` === target) {
    return false;
  }
  window.history.replaceState({}, "", target);
  return true;
}

/** Append sim params onto a URLSearchParams (e.g. cta-flags poll). */
export function appendGeoSimParamsToSearch(
  params: URLSearchParams,
  session?: GeoSimSession | null,
): void {
  const s = session === undefined ? readGeoSimSession() : session;
  if (!s) return;
  params.set(GEO_SIM_PARAM, "1");
  params.set(GEO_SIM_TOKEN_PARAM, s.token);
  // country may already be set from template config — prefer session for sim
  params.set("country", s.country);
  if (s.state) params.set("state", s.state);
  if (s.province) params.set("province", s.province);
  if (s.city) params.set("city", s.city);
  if (s.neighborhood) params.set("neighborhood", s.neighborhood);
  params.set("simulate_vpn", s.simulateVpn || "clean");
  if (s.simulateIp) params.set("simulate_ip", s.simulateIp);
}
